import { ContactDetailBody } from "@/components/contacts/ContactDetailSidebar.tsx";
import { useContact } from "@/hooks/useContacts.tsx";
import { CanvasModalCard } from "./CanvasModalCard.tsx";

/**
 * The canvas overlay for a contact target. Renders the shared
 * {@link ContactDetailBody} into the generic {@link CanvasModalCard} shell — the
 * same shell the source and artifact overlays use — so a contact link on a canvas
 * page fills the canvas. The contact chip still opens the right sidebar where
 * there is no canvas (mobile / gate off).
 *
 * The caller resolves the id from the canvas URN; the contact is fetched by id
 * via {@link useContact}. One layer of the modal stack, which owns dismissal.
 */
export const CanvasContactModal = ({
  contactId,
  container,
  showBack,
  isFullscreen,
  onBack,
  onClose,
}: {
  /** The targeted contact's id, or undefined when no contact is in the canvas. */
  contactId: string | undefined;
  /** The stack layer this modal portals into. */
  container: HTMLElement;
  /** Show the leading Back affordance (this layer sits above the stack root). */
  showBack: boolean;
  /** Whether the presented entity is shown fullscreen. */
  isFullscreen: boolean;
  /** Pop this layer. */
  onBack: () => void;
  /** Clear the whole stack. */
  onClose: () => void;
}) => {
  // Title only; `ContactDetailBody` owns the body's own `useContact` (deduped).
  const { contact } = useContact(contactId);

  // No contact targeted (query disabled): render no card.
  if (contactId === undefined) return null;

  return (
    <CanvasModalCard
      title={contact?.name || "Contact"}
      container={container}
      showBack={showBack}
      isFullscreen={isFullscreen}
      onBack={onBack}
      onClose={onClose}
    >
      <div className="px-4 py-4">
        <ContactDetailBody contactId={contactId} onDeleted={onBack} />
      </div>
    </CanvasModalCard>
  );
};
